import { useEffect, useState } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/frontend/components/ui/carousel";
import { Dialog, DialogContent, DialogTitle } from "@/frontend/components/ui/dialog";

export interface LightboxImage {
  url: string;
  caption?: string;
}

interface ImageLightboxProps {
  images: LightboxImage[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  startIndex?: number;
  title?: string;
}

/**
 * Fullscreen image viewer. Opens a looping carousel of `images` over a dark
 * overlay, starting at `startIndex`. The Dialog content remounts on each open,
 * so `startIndex` is applied fresh every time.
 */
export const ImageLightbox = ({
  images,
  open,
  onOpenChange,
  startIndex = 0,
  title = "Photo viewer",
}: ImageLightboxProps) => {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(startIndex);

  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
    };
  }, [api]);

  if (images.length === 0) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[100vw] sm:max-w-5xl w-full border-0 bg-transparent p-0 shadow-none text-white">
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <Carousel
          setApi={setApi}
          opts={{ loop: images.length > 1, startIndex }}
          className="w-full"
        >
          {/* Slide counter */}
          {images.length > 1 && (
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 rounded-full bg-black/60 px-3 py-1 text-sm font-medium text-white">
              {current + 1} / {images.length}
            </div>
          )}
          <CarouselContent>
            {images.map((image, index) => (
              <CarouselItem key={index}>
                {/* Clicking the area around the image closes the viewer;
                    clicking the image itself does not. */}
                <div
                  className="flex h-[85vh] items-center justify-center"
                  onClick={() => onOpenChange(false)}
                >
                  <img
                    src={image.url}
                    alt={image.caption || `Photo ${index + 1}`}
                    className="max-h-full max-w-full object-contain"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                {image.caption && (
                  <p className="mt-2 text-center text-sm text-white/80">
                    {image.caption}
                  </p>
                )}
              </CarouselItem>
            ))}
          </CarouselContent>
          {images.length > 1 && (
            <>
              <CarouselPrevious className="left-2 sm:left-4 top-1/2 -translate-y-1/2 text-foreground" />
              <CarouselNext className="right-2 sm:right-4 top-1/2 -translate-y-1/2 text-foreground" />
            </>
          )}
        </Carousel>
      </DialogContent>
    </Dialog>
  );
};
